import { Controller } from "@hotwired/stimulus";

// Orchestrates the unified affiliate program editor panel.
//
// Mobile (<= 50em): the products / invites / advanced sections live in nested
// <dialog>s that push in from the right as steps inside the hub sheet — they
// are navigation, not dismissable bottom sheets, so they carry neither a drag
// indicator nor swipe-to-dismiss (leaving via the back arrow reconciles).
// Desktop: the same DOM renders inline (the sheets are display: contents), so
// the sheets are never opened.
//
// The products and invites sections are real, separate forms that buffer their
// state server-side via the reconcile_* endpoints (form_drafts). Two rules keep
// those drafts in sync with the DOM:
//   1. Closing a sheet (back arrow / Escape) first reconciles a dirty sub-form
//      and only closes on success — a validation error keeps the sheet open,
//      showing the re-rendered errors. State is applied, never discarded.
//   2. Submitting the main form first flushes all dirty sub-forms, so the
//      creation/update services always read up-to-date drafts.
export default class extends Controller {
  static targets = ["sheet", "inviteCount", "sheetValue"];

  connect() {
    this.dirty = new Set();
    this.flushState = null;
    this.reconcilePromises = new Map();
    this.dockedPageScrollY = null;

    // Delegated listeners: a 422 reconcile replaces the sub-form element, so
    // per-form listeners would be lost.
    this.onInput = (event) => {
      const form = event.target.closest("form");
      if (form && this.subForms().includes(form)) this.dirty.add(form.id);
      this.mirrorSheetValue(event.target);
    };
    this.element.addEventListener("input", this.onInput);
    this.element.addEventListener("change", this.onInput);

    // Escape closes a <dialog> natively and would discard nothing visually but
    // skip the reconcile — route it through the same close path instead.
    this.onCancel = (event) => {
      const sheet = event.target;
      if (!this.sheetTargets.includes(sheet)) return;
      event.preventDefault();
      this.closeSheetElement(sheet);
    };
    this.element.addEventListener("cancel", this.onCancel);

    // Crossing to desktop mid-edit: the inline layout takes over, any open
    // sheet must not stay in the top layer.
    this.mediaQuery = window.matchMedia("(max-width: 50em)");
    this.onViewportChange = () => {
      if (!this.mediaQuery.matches) this.closeAllSheets();
    };
    this.mediaQuery.addEventListener("change", this.onViewportChange);

    // Browser back — including iOS' edge swipe, in Safari and the PWA — steps
    // back one sheet instead of leaving the page: each opened sheet pushes a
    // history entry, popstate closes the topmost open sheet through the same
    // applying path as the back arrow. Turbo ignores these entries (no turbo
    // state). The first sheet additionally pushes a base entry underneath:
    // without it, popping the last sheet lands on Turbo's own entry for the
    // page, whose popstate makes Turbo restore the page from its snapshot
    // cache — visually a second "swipe" right after the sheet closed. A back
    // on the hub pops that base entry and simply continues out.
    this.pushedSheets = 0;
    this.suppressPop = false;
    this.clearingHistory = false;
    this.closingViaHistory = false;
    this.onPopState = () => {
      if (this.pushedSheets === 0) return;
      if (this.clearingHistory) {
        // One popstate for the whole history.go(-n) jump of closeAllSheets.
        this.clearingHistory = false;
        this.pushedSheets = 0;
        return;
      }
      this.pushedSheets--;
      if (this.suppressPop) {
        this.suppressPop = false;
        return;
      }
      const open = [...this.sheetTargets].reverse().find((sheet) => sheet.open);
      if (!open) return; // the base entry popped: Turbo's own entry is the
      // destination and its restoration closes the editor — nothing to add.

      // One close per pop: a second swipe while a reconcile-close is still in
      // flight must not also close the sheet underneath.
      if (this.closingViaHistory) return;
      this.closingViaHistory = true;
      this.closeSheetElement(open, { viaHistory: true }).finally(() => {
        this.closingViaHistory = false;
      });
    };
    window.addEventListener("popstate", this.onPopState);

    // The list sheets dock a search bar at their bottom edge. When the on-screen
    // keyboard opens, iOS leaves the layout viewport (and with it `dvh`) at full
    // height, so a sheet sized in `dvh` would keep that bar behind the keyboard.
    // Track the visual viewport instead — same approach as sticky_cta_controller.
    // Only the sheet marked data-keyboard-docked consumes the value (see below).
    this.onVisualViewportChange = () => {
      const docked = this.sheetTargets.find((sheet) =>
        sheet.hasAttribute("data-keyboard-docked")
      );
      if (docked) this.scheduleDockedSheetSync(docked);
    };
    window.visualViewport?.addEventListener("resize", this.onVisualViewportChange);
    window.visualViewport?.addEventListener("scroll", this.onVisualViewportChange);

    // Two keyboard modes. Docked search inputs (products / invites): their
    // sheet follows the visual viewport while focused — the bar rides on the
    // keyboard, results stay tappable above it. Every other field: the sheet
    // keeps its height and the keyboard overlays it; iOS reveals the field by
    // panning. (Resizing on every focus squashed the whole sheet instead.)
    this.pendingReveal = null;
    this.dockSelector =
      ".affiliate-product-search__input, .affiliate-invites__input";
    this.onFocusIn = (event) => {
      if (!this.mediaQuery.matches) return; // desktop: no sheets, no keyboard

      const input = event.target.closest?.(this.dockSelector);
      const sheet = input?.closest("dialog");
      if (!sheet) return;

      this.dockedPageScrollY ??= window.scrollY;
      sheet.setAttribute("data-keyboard-docked", "");
      // Products: what the search filters is the list, but it sits below the
      // commission block — bring it to the top once the keyboard is up.
      this.pendingReveal = sheet.querySelector(
        ".affiliate-products-filter__header"
      );
      // Normally the keyboard's resize event sizes and settles the sheet. Two
      // staged refreshes cover the cases without a (final) event: hopping here
      // from another field (keyboard already up, nothing fires) and iOS
      // reporting a stale mid-animation height, which left the sheet floating
      // short of the keyboard's edge.
      this.settleTimers?.forEach(clearTimeout);
      this.settleTimers = [300, 700].map((delay) =>
        setTimeout(() => {
          if (sheet.hasAttribute("data-keyboard-docked")) {
            this.scheduleDockedSheetSync(sheet);
          }
        }, delay)
      );
      this.scheduleDockedSheetSync(sheet);
    };
    this.onFocusOut = (event) => {
      const input = event.target.closest?.(this.dockSelector);
      if (!input) return;

      this.pendingReveal = null;
      this.settleTimers?.forEach(clearTimeout);
      const sheet = input.closest("dialog");
      if (sheet) {
        sheet.removeAttribute("data-keyboard-docked");
        this.clearDockedSheetGeometry(sheet);
        // Whatever reveal-scroll iOS applied must not outlive the mode.
        sheet.scrollTop = 0;
      }
      this.restoreDockedPageScroll();
      this.dockedPageScrollY = null;
    };
    this.element.addEventListener("focusin", this.onFocusIn);
    this.element.addEventListener("focusout", this.onFocusOut);

    // After a validation-error re-render (the whole editor is replaced), the
    // errored section would be invisible on mobile — open its sheet.
    this.revealErroredSheet();
  }

  disconnect() {
    this.element.removeEventListener("input", this.onInput);
    this.element.removeEventListener("change", this.onInput);
    this.element.removeEventListener("cancel", this.onCancel);
    this.element.removeEventListener("focusin", this.onFocusIn);
    this.element.removeEventListener("focusout", this.onFocusOut);
    this.settleTimers?.forEach(clearTimeout);
    cancelAnimationFrame(this.viewportFrame);
    cancelAnimationFrame(this.viewportMeasureFrame);
    this.mediaQuery.removeEventListener("change", this.onViewportChange);
    window.removeEventListener("popstate", this.onPopState);
    window.visualViewport?.removeEventListener(
      "resize",
      this.onVisualViewportChange
    );
    window.visualViewport?.removeEventListener(
      "scroll",
      this.onVisualViewportChange
    );
    this.restoreDockedPageScroll();
    this.sheetTargets.forEach((sheet) => this.clearDockedSheetGeometry(sheet));
  }

  revealErroredSheet() {
    if (!this.mediaQuery.matches) return;

    this.openSheetChain(this.element.querySelector(".field_with_errors"));
  }

  // Opens every sheet enclosing `element`, outermost first — the advanced
  // section nests one sheet per drill-down row, so a field can sit two levels in.
  openSheetChain(element) {
    if (!element) return;

    const chain = [];
    for (let node = element.closest("dialog"); node; node = node.parentElement?.closest("dialog")) {
      if (this.sheetTargets.includes(node)) chain.unshift(node);
    }
    chain.forEach((sheet) => {
      if (!sheet.open) {
        sheet.showModal();
        this.focusSheet(sheet);
        this.pushSheetHistory();
      }
    });
  }

  // WebKit pans the visual viewport while the keyboard opens, but a top-layer
  // dialog does not expose one stable coordinate system during that animation.
  // Moving the sheet by offsetTop can therefore apply the pan twice. Keep its
  // top fixed and size it to the visual viewport's bottom coordinate instead:
  // the search bar then meets the keyboard whether WebKit pans the dialog or
  // already presents it relative to the visual viewport. Double-rAF waits for
  // the same stable post-paint boundary used by the comment composer.
  scheduleDockedSheetSync(sheet) {
    cancelAnimationFrame(this.viewportFrame);
    cancelAnimationFrame(this.viewportMeasureFrame);

    this.viewportFrame = requestAnimationFrame(() => {
      this.viewportMeasureFrame = requestAnimationFrame(() => {
        if (!sheet.hasAttribute("data-keyboard-docked")) return;

        this.syncDockedSheetGeometry(sheet);
      });
    });
  }

  syncDockedSheetGeometry(sheet) {
    const viewport = window.visualViewport;
    if (!viewport?.height) return;

    sheet.style.setProperty(
      "--editor-viewport-height",
      `${viewport.offsetTop + viewport.height}px`
    );
    this.restoreDockedPageScroll();

    // iOS scrolls even overflow:hidden ancestors to reveal a focused field.
    // The docked layout already keeps it above the keyboard, so undo that
    // internal scroll. Reveal first because scrollIntoView can touch it again.
    if (this.pendingReveal) {
      this.pendingReveal.scrollIntoView({ block: "start" });
      this.pendingReveal = null;
    }
    sheet.scrollTop = 0;
  }

  clearDockedSheetGeometry(sheet) {
    sheet.style.removeProperty("--editor-viewport-height");
  }

  restoreDockedPageScroll() {
    if (this.dockedPageScrollY === null) return;
    if (Math.abs(window.scrollY - this.dockedPageScrollY) < 0.5) return;

    window.scrollTo(window.scrollX, this.dockedPageScrollY);
  }

  // showModal() focuses the sheet's first focusable element — the back button —
  // and paints the UA focus ring around it (iOS: a blue halo). Focus the sheet
  // itself instead, same as modal_controller: neutral, announced by screen
  // readers, and Tab still enters the content.
  focusSheet(sheet) {
    if (sheet.querySelector("[autofocus]")) return;

    sheet.setAttribute("tabindex", "-1");
    sheet.focus({ preventScroll: true });
  }

  // Drill-down rows ("Tracking-Laufzeit 365 Tage ›") show the value of the field
  // inside their sheet; keep it in sync while that field is edited.
  mirrorSheetValue(field) {
    const sheet = field.closest?.("dialog[data-sheet-name]");
    if (!sheet) return;

    const label = this.sheetValueTargets.find(
      (target) => target.dataset.sheetName === sheet.dataset.sheetName
    );
    if (!label) return;

    const value = field.value.trim();
    const empty = value === "" || value === "0";
    const { emptyLabel, suffix } = label.dataset;
    label.textContent = empty ? emptyLabel : [value, suffix].filter(Boolean).join(" ");
  }

  // Fired by the affiliate-invites controller whenever a row is added or
  // removed; keeps the count labels in sync. The count renders twice — the
  // mobile hub row and the desktop label row inside the members card.
  updateInviteCount(event) {
    const count = event.detail.count;

    this.inviteCountTargets.forEach((target) => {
      const { labelOne, labelOther, labelEmpty } = target.dataset;
      let label = labelEmpty;
      if (count === 1) label = labelOne;
      if (count > 1) label = labelOther.replace("COUNT", count);

      target.textContent = label;
      target.classList.toggle("affiliate-editor__hub-value--muted", count === 0);
    });
  }

  openSheet(event) {
    const sheet = this.sheetTargets.find(
      (candidate) => candidate.dataset.sheetName === event.params.sheet
    );
    if (sheet && !sheet.open) {
      sheet.showModal();
      this.focusSheet(sheet);
      this.pushSheetHistory();
    }
  }

  pushSheetHistory() {
    if (!this.mediaQuery.matches) return; // desktop renders inline, no steps

    if (this.pushedSheets === 0) {
      history.pushState({ affiliateEditorBase: true }, "");
      this.pushedSheets++;
    }
    history.pushState({ affiliateEditorSheet: true }, "");
    this.pushedSheets++;
  }

  closeSheet(event) {
    const sheet = event.target.closest("dialog");
    if (sheet) this.closeSheetElement(sheet);
  }

  // Back arrow inside a sheet: apply, don't discard. Sub-form sheets reconcile
  // their draft first and only close on success; the advanced sheet holds main
  // form fields (they submit with the main form) and closes directly.
  // History stays in step: a close initiated here consumes the sheet's history
  // entry; one arriving via popstate has already lost it — and re-pushes it if
  // a failed reconcile keeps the sheet open.
  async closeSheetElement(sheet, { viaHistory = false } = {}) {
    const subForm = sheet.querySelector("form");
    if (subForm && this.dirty.has(subForm.id)) {
      const ok = await this.reconcile(subForm);
      if (!ok) {
        if (viaHistory) {
          history.pushState({ affiliateEditorSheet: true }, "");
          this.pushedSheets++;
        }
        return;
      }
    }
    sheet.close();
    // > 1: the base entry underneath stays until the editor itself is left.
    if (!viaHistory && this.pushedSheets > 1) {
      this.suppressPop = true;
      history.back();
    }
  }

  // Intercepts the main form submission (footer CTA) and flushes dirty
  // sub-forms first. Re-entrant: after a successful flush the form is
  // re-submitted and passes straight through.
  beforeMainSubmit(event) {
    if (this.flushState === "done") {
      this.flushState = null;
      return;
    }

    const dirtyForms = this.subForms().filter((form) => this.dirty.has(form.id));
    if (dirtyForms.length === 0) return;

    event.preventDefault();
    if (this.flushState === "flushing") return; // double-submit guard
    this.flushState = "flushing";

    const mainForm = event.target;
    (async () => {
      for (const form of dirtyForms) {
        const ok = await this.reconcile(form);
        if (!ok) {
          this.flushState = null;
          this.revealForm(form.id);
          return;
        }
      }
      this.flushState = "done";
      mainForm.requestSubmit();
    })();
  }

  // Submits a sub-form and resolves with whether the reconcile succeeded.
  // Listens on the document because a 422 response replaces the form element.
  reconcile(form) {
    const pending = this.reconcilePromises.get(form.id);
    if (pending) return pending;

    const promise = new Promise((resolve) => {
      const onEnd = (event) => {
        if (event.target.id !== form.id) return;
        document.removeEventListener("turbo:submit-end", onEnd);
        if (event.detail.success) this.dirty.delete(form.id);
        resolve(event.detail.success);
      };
      document.addEventListener("turbo:submit-end", onEnd);
      form.requestSubmit();
    });
    this.reconcilePromises.set(form.id, promise);
    promise.finally(() => this.reconcilePromises.delete(form.id));

    return promise;
  }

  // After a failed reconcile: surface the section — on mobile open its sheet,
  // then scroll the first errored field into view.
  revealForm(formId) {
    const form = document.getElementById(formId);
    if (!form) return;

    if (this.mediaQuery.matches) this.openSheetChain(form);
    form
      .querySelector(".field_with_errors")
      ?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  subForms() {
    return this.sheetTargets
      .map((sheet) => sheet.querySelector("form"))
      .filter(Boolean);
  }

  closeAllSheets() {
    if (this.pushedSheets > 0) {
      this.clearingHistory = true;
      history.go(-this.pushedSheets);
    }
    this.sheetTargets.forEach((sheet) => {
      if (sheet.open) sheet.close();
    });
  }
}
